/* eslint-disable object-curly-newline */
import Promise from 'bluebird';
import _ from 'underscore';
import { StringPayload } from '@bingads-webui/azure-upload';
import { CommonImportRepository } from '../common-repository';
import { filterAndLocalizeFacebookCampaignsUtil } from './facebook-import-repository-utils';
import { getLocalizedCampaignTypeStringUtil, containDisplayTypeUtil } from '../import-repository-utils';
import { FacebookLoginRepository } from './facebook-login-repository';

const getFacebookSessionUrlTmpl =
  'ImportSession/StartFacebookSession(CustomerId=:customer_id,AccountId=:account_id)?accessToken={{accessToken}}';
const getFacebookSessionWithTokenUrlTmpl =
  'ImportSession/StartFacebookSession(CustomerId=:customer_id,AccountId=:account_id)?LongLivedAccessToken={{LongLivedAccessToken}}';
const getFacebookAccountsUrlTmpl =
  '/ImportSession/FacebookAccounts(CustomerId=:customer_id,AccountId=:account_id,SessionId={{SessionId}})';
const getFacebookCampaignsUrlTmpl =
  '/ImportSession/FacebookCampaigns(CustomerId=:customer_id,AccountId=:account_id,SessionId={{SessionId}})';
const getFacebookAdGroupsUrlTmpl =
  '/ImportSession/FacebookAdGroups(CustomerId=:customer_id,AccountId=:account_id,SessionId={{SessionId}})';
const importType = 'FacebookImport';
const keyNameOfSessionIdInCookie = 'OAuth2_FacebookSessionId';
const keyNameOfLoginAccountInCookie = 'OAuth2_FacebookLoginAccount';
const facebookEntitiesBulkFileTypeNum = 17;
const facebookAdsEntityBulkFileType = 'FacebookAdsEntityIdFile';

// permission name => description used to explain why we need this permisison to user
const permissionsDescription = {
  email: 'FacebookImport_Permission_Email_Desc', // email is required to identify Facebook users
  ads_read: 'FacebookImport_Permission_Ads_Read_Desc', // ads_read is used to read Meta Ads
  // ads_management: 'used to manage Meta Ads',
};

export class FacebookImportRepository extends CommonImportRepository {
  constructor({ deps }) {
    super({ deps, type: 'FacebookImport', importType });

    if (deps != null) {
      this.edm = deps.edm;
      this.jsData = deps.jsData;
      this.isMultiAccountImport = deps.isMultiAccountImport;
      if (this.edm != null) {
        this.taskItemOptions = {
          adapter: 'odata',
          entitySet: this.isMultiAccountImport ?
            this.edm.currentCustomer.TaskItems : this.edm.currentAccount.TaskItems,
        };
      }
      this.loginRepository = new FacebookLoginRepository({ deps });
    }
  }

  startFacebookSession(authCode) {
    const url = getFacebookSessionUrlTmpl.replace('{{accessToken}}', authCode);

    return this.startFacebookSessionInternal(url);
  }

  startFacebookSessionWithToken(token) {
    const url = getFacebookSessionWithTokenUrlTmpl.replace('{{LongLivedAccessToken}}', token);

    return this.startFacebookSessionInternal(url);
  }

  startFacebookSessionInternal(url) {
    return this.odata.post(url, { data: '' })
      .then((data) => {
        this.logTrace(`StartFacebookSession response with ${JSON.stringify(data)}`);
        const { FacebookEmail, FacebookSessionId, FacebookUserId } = data;
        if (FacebookEmail && FacebookSessionId) {
          this.setFacebookImportAccountInfoInCookie({
            sessionId: FacebookSessionId,
            loginAccount: FacebookEmail,
          });
        }
        return {
          sessionId: FacebookSessionId,
          userId: FacebookUserId,
          loginAccount: FacebookEmail,
        };
      });
  }

  getFacebookCampaigns(facebookCustomerId) {
    const sessionId = this.getFacebookSessionIdFromCookie();
    let url = getFacebookCampaignsUrlTmpl.replace('{{SessionId}}', sessionId);

    url = this.formatUrl({
      url,
      queryParameters: {
        externalAccountId: facebookCustomerId,
      },
    });

    return this.getFacebookCampaignsInternal(url);
  }

  getFacebookCampaignsInternal(url) {
    return this.odata.get(url)
      .then(data => filterAndLocalizeFacebookCampaignsUtil(data, this.i18n, this.logError));
  }

  getFacebookAdGroupsByBlobName(facebookCustomerId, blobName, usecache = true) {
    const sessionId = this.getFacebookSessionIdFromCookie();
    let url = getFacebookAdGroupsUrlTmpl.replace('{{SessionId}}', sessionId);

    url = this.formatUrl({
      url,
      queryParameters: {
        facebookCustomerId,
        blobName,
        usecache,
      },
    });

    return this.odata.get(url);
  }

  containDisplayType(campaignsList) {
    return containDisplayTypeUtil(campaignsList, this.i18n);
  }

  getLocalizedCampaignTypeString(type) {
    return getLocalizedCampaignTypeStringUtil(type, this.i18n);
  }

  getFacebookAccounts(currentSessionId = null) {
    const sessionId = currentSessionId || this.getFacebookSessionIdFromCookie();
    const url = getFacebookAccountsUrlTmpl.replace('{{SessionId}}', sessionId);

    return this.odata.get(url);
  }

  getFacebookSessionIdFromCookie() {
    return this.getValueInSubImportCookieByAccountId(keyNameOfSessionIdInCookie)[0];
  }

  getFacebookLoginAccountFromCookie() {
    return this.getValueInSubImportCookieByAccountId(keyNameOfLoginAccountInCookie)[0];
  }

  // this is for fetching multiple items from cookie
  getFacebookImportInfoFromCookie() {
    const result = this.getValueInSubImportCookieByAccountId(
      keyNameOfSessionIdInCookie,
      keyNameOfLoginAccountInCookie
    );

    return {
      facebookSessionId: result[0],
      facebookAccountName: result[1],
    };
  }

  isUserLoggedIn() {
    return !!this.getFacebookSessionIdFromCookie();
  }

  getFacebookAccountName() {
    return this.getFacebookLoginAccountFromCookie();
  }

  setFacebookImportLoginAccountInCookie(loginAccount) {
    this.addSubCookieToImportCookie({
      [keyNameOfLoginAccountInCookie]: loginAccount,
    });
  }

  setFacebookImportAccountInfoInCookie({ loginAccount, sessionId }) {
    this.addSubCookieToImportCookie({
      [keyNameOfLoginAccountInCookie]: loginAccount,
      [keyNameOfSessionIdInCookie]: sessionId,
    });
  }

  serializeFacebookEntitiesSelectionDataToStringPayload(selectionData) {
    return new StringPayload({
      content: JSON.stringify({
        CampaignIds: selectionData.selectedCampaignIds,
        AdGroupIdMap: selectionData.selectedAdGroupMap,
        CampaignIdsWithActiveAdGroups: selectionData.selectedWithActiveOnly,
      }),
    });
  }

  convertAdGroupDataToIdMap(adGroupData) {
    return _.mapObject(_.groupBy(adGroupData, 'campaignId'), arr => _.pluck(arr, 'Id'));
  }

  getCleanedFacebookAdGroupSelectionDataFromBlob(customerId, blobName) {
    return this.getFacebookAdGroupsByBlobName(customerId, blobName)
      .then(adGroupData => this.convertAdGroupDataToIdMap(adGroupData));
  }

  getSelectionDataFromBlob(blobName) {
    return this.getEntitiesBlob(blobName)
      .then(blob =>
        ({
          selectedCampaignIds: blob.CampaignIds,
          selectedAdGroupMap: blob.AdGroupIdMap,
          selectedWithActiveOnly: blob.CampaignIdsWithActiveAdGroups,
        }));
  }

  getEntitiesBlob(blobName) {
    return this.getBulkBlob(blobName, facebookEntitiesBulkFileTypeNum);
  }

  // add clean up campaigns in here
  prepareForCampaignSelectionStep(customerId, blobName) {
    const promises = {};
    promises.facebookCampaigns = this.getFacebookCampaigns(customerId);

    if (blobName) {
      promises.selectionData = this.getSelectionDataFromBlob(blobName)
        .then((selectionData) => {
          const finalState = {};
          finalState.selectedCampaignIds = selectionData.selectedCampaignIds;
          finalState.selectedWithActiveOnly = selectionData.selectedWithActiveOnly;

          if (!_.isEmpty(selectionData.selectedAdGroupMap)) {
            return this.getCleanedFacebookAdGroupSelectionDataFromBlob(customerId, blobName)
              .then((selectedAdGroupMap) => {
                finalState.selectedAdGroupMap = selectedAdGroupMap;
                return finalState;
              });
          }

          finalState.selectedAdGroupMap = {};
          return finalState;
        });
    }

    return Promise.props(promises)
      .then(({ facebookCampaigns, selectionData }) => {
        this.logTrace(`FacebookCampaignsCount: ${Object.keys(facebookCampaigns).length}`);
        const finalState = { facebookCampaigns };
        if (selectionData) {
          const availableIdBoolMap = _.object(_.map(facebookCampaigns, obj => [obj.Id, true]));
          const campaignIdFilter = ids => _.filter(ids, id => availableIdBoolMap[id]);
          const selectedCampaignIds = campaignIdFilter(selectionData.selectedCampaignIds);
          const selectedWithActiveOnly = campaignIdFilter(selectionData.selectedWithActiveOnly);
          _.extend(finalState, {
            selectedCampaignIds: _.isEmpty(selectedCampaignIds) ? null : selectedCampaignIds,
            selectedWithActiveOnly: _.isEmpty(selectedWithActiveOnly)
              ? null : selectedWithActiveOnly,
            selectedAdGroupMap: selectionData.selectedAdGroupMap,
          });
        }
        return finalState;
      });
  }

  prepareFacebookImportContext(
    facebookCustomerId,
    campaignIds,
    initialAdWordsAccount,
    facebookEntitiesSelectionData,
    uid
  ) {
    let sessionId;
    let facebookAccountName;

    if (initialAdWordsAccount) {
      ({ sessionId } = initialAdWordsAccount);
      ({ facebookAccountName } = initialAdWordsAccount);
    } else {
      const info = this.getFacebookImportInfoFromCookie();

      sessionId = info.facebookSessionId;
      ({ facebookAccountName } = info);
    }

    const facebookImportContext = {
      '@odata.type': '#Model.FacebookImportContext',
      SessionId: sessionId,
      AccountId: facebookCustomerId,
      AccountName: facebookAccountName,
      CampaignIds: campaignIds || [],
      FacebookSessionUserId: uid,
    };

    if (facebookEntitiesSelectionData) {
      return this.uploadToAzureBlobViaOdata({
        bulkFileType: facebookAdsEntityBulkFileType,
        payload:
          this.serializeFacebookEntitiesSelectionDataToStringPayload(facebookEntitiesSelectionData),
      }).then(blob =>
        ({
          ...facebookImportContext,
          FacebookEntitiesBlobName: blob.BlobName,
        }));
    }

    return Promise.resolve(facebookImportContext);
  }

  prepareFacebookImportTaskItem({
    importOptions,
    stepState,
  }) {
    const {
      importUserPreferences,
      notificationType,
      notificationEmailAddresses,
      timeZoneId,
      name,
      cron,
      taskItemState,
      ScenarioIdentifier,
    } = importOptions;

    const {
      facebookCustomerId,
      campaignIds,
      initialAdWordsAccount,
      facebookEntitiesSelectionData,
    } = stepState;

    return this.prepareFacebookImportContext(
      facebookCustomerId, campaignIds, initialAdWordsAccount,
      facebookEntitiesSelectionData, this.uid
    )
      .then(facebookImportContext =>
        (_.extend(
          this.prepareCompleteImportTaskItem(importUserPreferences, timeZoneId), {
            '@odata.type': '#Model.FacebookTaskItem',
            Type: `FacebookImport${this.testTypeSuffix}`,
            NotificationType: notificationType,
            NotificationEmailAddresses: notificationEmailAddresses,
            FacebookImportContext: facebookImportContext,
            ImportType: importType,
          },
          _.pick(importOptions, 'IsAutoFrequency', 'IsSmartImport'),
          !!name && { Name: name },
          !!taskItemState && { State: taskItemState },
          !!cron && { Cron: cron },
          !!ScenarioIdentifier && { ScenarioIdentifier }
        )));
  }

  refreshScheduledFacebookImportTaskItemSessionId(accessToken, taskItemId, currentUser) {
    return this.startFacebookSession(accessToken).then(({ sessionId }) => {
      const facebookSessionId = sessionId || this.getFacebookSessionIdFromCookie();
      if (!facebookSessionId) {
        return Promise.reject(new Error('failed to get facebook import session id.'));
      }

      return this.getImportTaskItem(taskItemId).then((taskItem) => {
        const data = {
          ...taskItem,
          FacebookImportContext: {
            ...taskItem.FacebookImportContext,
            SessionId: facebookSessionId,
            FacebookSessionUserId: (currentUser && currentUser.Id)
              || taskItem.FacebookImportContext.FacebookSessionUserId,
          },
        };
        return this.updateScheduledImportTaskItem(data, taskItemId);
      });
    });
  }

  setInProcessImportCookie({ importId }) {
    super.setInProcessImportCookie({
      importId,
      importType,
    });
  }

  getFacebookImportUserPreferences() { // facebookCustomerId
    let preferences = {};

    return this.getImportUserPreferencesWithBMCandMinInfo(importType)
      .then((userPref) => {
        preferences = _.extend(preferences, userPref);

        return preferences;
      })
      .then((result) => {
        if (result === false) {
          return false;
        }
        const { TrackingTemplate, FinalUrlSuffix } = result;
        preferences = _.extend(
          preferences,
          {
            TrackingTemplateValue: TrackingTemplate,
            FinalUrlSuffixValue: FinalUrlSuffix,
            AdWordsAccountTrackingTemplate: TrackingTemplate,
            AdWordsAccountFinalUrlSuffix: FinalUrlSuffix,
          }
        );
        return preferences;
      });
  }

  login({
    rerequest = false,
    onSuccess = _.noop,
    onPermissionsDeclined = _.noop,
    onFail = _.noop,
  }) {
    this.loginRepository.login({
      permissions: _.keys(permissionsDescription),
      rerequest,
      onSuccess: ({ declinedScopes, accessToken, userID }) => {
        if (declinedScopes && declinedScopes.length > 0) {
          // some of the requests are declined by users, show messages to explain
          // why we need these permissions and ask users to re-request
          const declinedScopesAndDesc
            = declinedScopes.map(ds => ({
              name: ds,
              description: this.i18n.get(permissionsDescription[ds]),
            }));
          onPermissionsDeclined({ declinedScopes: declinedScopesAndDesc, userID, accessToken });
        } else {
          onSuccess({ userID, accessToken });
        }
      },
      onFail: (error) => {
        // todo: this.logError(error);
        onFail(error);
      },
    });
  }

  logout({ onSuccess = _.noop, onFail = _.noop } = {}) {
    this.removeImportCookieValue(keyNameOfSessionIdInCookie, keyNameOfLoginAccountInCookie);
    this.loginRepository.logout({ onSuccess, onFail });
  }

  checkOpportunityType() {
    const { opportunityType } = this.deps;

    return opportunityType === 'FacebookImportOpportunity';
  }
}
